import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle } from "lucide-react";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/Dialog";
import { toast } from "@/components/ui/Toast";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// ApproveDialog
//
// Modal to approve an IN_REVIEW assessment.
// Requires a security comment (min 1 char).
// Design: §6.2.2 — Approve action with comment
// ─────────────────────────────────────────────

interface ApproveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentId: string;
  onSuccess?: () => void;
}

export function ApproveDialog({
  open,
  onOpenChange,
  assessmentId,
  onSuccess,
}: ApproveDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState("");

  const mutation = useMutation({
    mutationFn: (body: { comment: string }) =>
      api.post(`/assessments/${assessmentId}/approve`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessment", assessmentId] });
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
      toast.success(t("assessment.approveDialog.approvedSuccess"));
      onOpenChange(false);
      setComment("");
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast.error(err.message ?? t("assessment.approveDialog.approveFailed"));
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!comment.trim()) return;
    mutation.mutate({ comment: comment.trim() });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full"
              style={{ backgroundColor: "var(--success-bg)" }}
            >
              <CheckCircle className="h-5 w-5" style={{ color: "var(--success)" }} />
            </div>
            <DialogTitle>{t("assessment.approveDialog.title")}</DialogTitle>
          </div>
          <DialogDescription>
            {t("assessment.approveDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-[14px] font-medium text-text-secondary">
              {t("assessment.approveDialog.commentLabel")} <span className="text-danger">*</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("assessment.approveDialog.commentPlaceholder")}
              rows={4}
              required
              className="min-h-[100px] w-full rounded-control border border-border-default bg-bg-input px-3 py-2 text-[14px] text-text-primary placeholder:text-text-muted resize-y focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent"
            />
            <p className="text-[12px] text-text-muted">
              {t("assessment.approveDialog.commentHelp")}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                onOpenChange(false);
                setComment("");
              }}
              disabled={mutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              isLoading={mutation.isPending}
              disabled={!comment.trim()}
              style={{ backgroundColor: "var(--success)" }}
            >
              {t("assessment.approveDialog.title")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
